import React, { useEffect, useMemo, useState, forwardRef } from "react";
import TableContainer from "Common/TableContainer";
import { Download } from "lucide-react";
import {
  getWarrantyDocumentByWarrantyId,
  getWarrantyDocumentByFilename,
} from "../../../helpers/fakebackend_helper";
import { ToastContainer } from "react-toastify";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../../context/LoaderContext";

interface WarrantyDocumentDTO {
  id: number;
  path: string;
  description: string;
  warranty: any;
}

const WarrantyDocument = forwardRef(
  (
    {
      warrantyId,
      onClose,
    }: { warrantyId?: string; onClose?: () => void },
    ref
  ) => {
    const [data, setData] = useState<WarrantyDocumentDTO[]>([]);
    const { showLoader, hideLoader } = useLoader();

    /**
     * Fetch warranty documents by warrantyId
     */
    const fetchWarrantyDocuments = async () => {
      if (!warrantyId) return;

      showLoader();
      try {
        const response = await getWarrantyDocumentByWarrantyId({ warrantyId });
        const documents = response.data?._embedded?.warrantyDocuments || [];
        setData(documents);
      } catch (error) {
        toast.error("Error al obtener los documentos ID: " + warrantyId + error, {
          autoClose: 2000,
        });
      } finally {
        hideLoader();
      }
    };

    useEffect(() => {
      if (warrantyId) {
        fetchWarrantyDocuments();
      }
    }, [warrantyId]);

    const handleDownloadFile = async (path: string) => {
      if (!path) return;

      showLoader();
      try {
        const response = await getWarrantyDocumentByFilename(path);
        const contentDisposition = response.headers["content-disposition"];
        const filename = contentDisposition
          ? contentDisposition.split("filename=")[1].replace(/"/g, "")
          : path;

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast.error("Error al descargar el archivo: " + error, {
          autoClose: 2000,
        });
      } finally {
        hideLoader();
      }
    };

    /**
     * Columns definition
     */
    const columns = useMemo(
      () => [
        {
          header: "Nombre del archivo",
          accessorKey: "path",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Descripción",
          accessorKey: "description",
          enableColumnFilter: false,
          enableSorting: false,
        },
        {
          header: "Acciones",
          cell: ({ row }: any) => (
            <div className="flex justify-center">
              <button
                onClick={() => handleDownloadFile(row.original.path)}
                className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md bg-slate-100 text-slate-500 hover:text-custom-500 hover:bg-custom-100 dark:bg-zink-600 dark:text-zink-200 dark:hover:bg-custom-500/20 dark:hover:text-custom-500"
              >
                <Download className="inline-block size-3" />
              </button>
            </div>
          ),
        },
      ],
      []
    );

    return (
      <div className="card">
        <ToastContainer closeButton={false} limit={1} />

        <div className="card-body">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Documentos Asociados</h2>
            <button
              className="btn bg-red-500 text-white"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>

          <TableContainer
            isPagination={true}
            isSelect={false}
            isGlobalFilter={false}
            columns={columns}
            data={data}
            customPageSize={5}
            divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
            tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
            theadclassName="border-b border-slate-200 dark:border-zink-500"
            trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
            thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
            tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
            PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
          />
        </div>
      </div>
    );
  }
);

export default WarrantyDocument;
